import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { Terrain } from '../game/Terrain';
import { readTune } from '../ui/TuneStore';

interface LawnMowerOptions {
  /** Half-width of the rectangular mowing area (metres). Default: 36. */
  yardHalf?: number;
  /** Time-of-day at which each sweep starts (0..1, 0.375 = 9 AM). */
  startTimeOfDay?: number;
  /** In-game days between sweeps. Default: 30 (first on day 0). */
  periodDays?: number;
}

import { assetUrl as _assetUrl } from '../ui/AssetMap';
const MODEL_SLOT = 'lawn-mower';
const MODEL_URL_DEFAULT = '/assets/lawn-mower.glb';
const TUNE_ID = 'lawn-mower';
const TUNE_DEFAULTS = { scale: 0.5, rotX: 0, rotY: 0, rotZ: 0, offY: 0 };

/**
 * Lawnmower hazard. On its schedule it enters a corner of the yard and mows
 * a complete serpentine pattern covering the full ±yardHalf square. While
 * mowing it leaves a damage swath and scatters nearby ants. Between sweeps
 * the group is hidden.
 *
 * Exposes `active`, `x`, `z`, `heading`, `width`, `length` for the minimap.
 */
export class LawnMower {
  readonly group: THREE.Group;

  private readonly terrain: Terrain;
  private readonly yardHalf: number;
  private readonly startTimeOfDay: number;
  private readonly periodDays: number;
  private nextSweepDay = 0;
  private lastTimeOfDay = -1;

  private readonly speed = 3;              // metres / second
  readonly damageRadius = 1.5;
  private readonly damageDps = 8;
  private readonly scatterRadius = 3;
  private readonly scatterForce = 1.4;
  private readonly noiseRadius = 6;
  private readonly noiseForce = 0.35;
  /** How many straight-line crossings make up a sweep. */
  private readonly crossingsPerSweep = 5;

  /** Visual footprint for the minimap rectangle (metres). */
  readonly width = 3.0;
  readonly length = 4.0;

  private _active = false;
  private mixer: THREE.AnimationMixer | null = null;

  /** Current mower position (world XZ). */
  private readonly pos = new THREE.Vector3();
  /** Unit travel direction (world XZ). */
  private readonly dir = new THREE.Vector3();
  private crossingsLeft = 0;

  constructor(terrain: Terrain, opts?: LawnMowerOptions) {
    this.terrain = terrain;
    this.yardHalf = opts?.yardHalf ?? 36;
    this.startTimeOfDay = opts?.startTimeOfDay ?? 0.375; // 9 AM
    this.periodDays = opts?.periodDays ?? 30;

    this.group = new THREE.Group();
    this.group.name = 'lawnmower';
    this.group.visible = false;
    void this.loadModel();
  }

  get active(): boolean { return this._active; }
  get x(): number { return this.pos.x; }
  get z(): number { return this.pos.z; }
  /** Heading in world radians — angle of travel direction in the XZ plane. */
  get heading(): number { return Math.atan2(this.dir.z, this.dir.x); }

  update(
    dt: number,
    elapsed: number,
    timeOfDay: number,
    dayNumber: number,
    damage: (x: number, z: number, radius: number, dps: number, dt: number) => number,
    scatter: (x: number, z: number, radius: number, force?: number) => void,
    /** Instant-kill for anything inside the blade radius — ants, spiders,
     *  grasshoppers. Called each tick with the current mower centre. */
    killArea?: (x: number, z: number, radius: number) => void,
  ): void {
    if (!this._active) {
      // Fire on the rising edge of 9 AM on a scheduled day. `lastTimeOfDay` is
      // set after the check so the edge only triggers once per day even if the
      // game pauses across it.
      const prev = this.lastTimeOfDay;
      const crossed = prev >= 0 && prev < this.startTimeOfDay && timeOfDay >= this.startTimeOfDay && timeOfDay < this.startTimeOfDay + 0.25;
      this.lastTimeOfDay = timeOfDay;
      if (crossed && dayNumber >= this.nextSweepDay) {
        this.startSweep();
      }
      void elapsed;
      return;
    }

    // Advance along travel direction.
    this.pos.x += this.dir.x * this.speed * dt;
    this.pos.z += this.dir.z * this.speed * dt;

    // Instant kill under the blade; broad damage + scatter around the cone of fear.
    killArea?.(this.pos.x, this.pos.z, this.damageRadius);
    damage(this.pos.x, this.pos.z, this.damageRadius, this.damageDps, dt);
    scatter(this.pos.x, this.pos.z, this.scatterRadius, this.scatterForce);
    scatter(this.pos.x, this.pos.z, this.noiseRadius, this.noiseForce);

    // Place on terrain and face travel direction.
    const y = this.terrain.getHeight(this.pos.x, this.pos.z) + 0.05;
    this.group.position.set(this.pos.x, y, this.pos.z);
    this.group.rotation.y = -Math.atan2(this.dir.z, this.dir.x);

    // Spin the wheels via the embedded animation clip.
    this.mixer?.update(dt);

    // End-of-crossing: off the ±yardHalf square → burn one crossing, either
    // start the next at a fresh random entry/angle or end the whole sweep.
    const outside =
      this.pos.x > this.yardHalf || this.pos.x < -this.yardHalf ||
      this.pos.z > this.yardHalf || this.pos.z < -this.yardHalf;
    if (outside) {
      this.crossingsLeft -= 1;
      if (this.crossingsLeft <= 0) {
        this.endSweep(elapsed);
      } else {
        this.pickNewCrossing();
      }
    }
  }

  private startSweep(): void {
    this.crossingsLeft = this.crossingsPerSweep;
    this.pickNewCrossing();
    this._active = true;
    this.group.visible = true;
  }

  /** Place the mower on a random edge of the yard with a random direction
   *  pointing generally inward, so it always cuts across the field. */
  private pickNewCrossing(): void {
    const half = this.yardHalf;
    const side = Math.floor(Math.random() * 4);
    const t = (Math.random() * 2 - 1) * half * 0.95;
    switch (side) {
      case 0: this.pos.set(-half, 0, t); break; // enter from -X edge
      case 1: this.pos.set( half, 0, t); break; // +X edge
      case 2: this.pos.set(t, 0, -half); break; // -Z edge
      default: this.pos.set(t, 0, half); break; // +Z edge
    }
    // Direction points toward origin, rotated by up to ±70° so crossings fan
    // out instead of all going through the centre.
    const toCentre = Math.atan2(-this.pos.z, -this.pos.x);
    const jitter = (Math.random() - 0.5) * (Math.PI * 70 / 180) * 2;
    const angle = toCentre + jitter;
    this.dir.set(Math.cos(angle), 0, Math.sin(angle));
  }

  private endSweep(elapsed: number): void {
    this._active = false;
    this.group.visible = false;
    this.nextSweepDay += this.periodDays;
    void elapsed;
  }

  private async loadModel(): Promise<void> {
    let gltf;
    try {
      gltf = await new GLTFLoader().loadAsync(_assetUrl(MODEL_SLOT, MODEL_URL_DEFAULT));
    } catch (err) {
      console.warn('[LawnMower] GLB load failed:', err);
      return;
    }
    const scene = gltf.scene;

    const tune = readTune(TUNE_ID, TUNE_DEFAULTS);
    scene.rotation.set(tune.rotX, tune.rotY, tune.rotZ);
    scene.scale.setScalar(tune.scale);
    scene.updateMatrixWorld(true);

    const bb = new THREE.Box3().setFromObject(scene);
    const c = new THREE.Vector3();
    bb.getCenter(c);
    scene.position.set(-c.x, -bb.min.y + tune.offY, -c.z);
    scene.updateMatrixWorld(true);

    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true;
      m.receiveShadow = true;
    });

    this.group.add(scene);

    // Drive the embedded clip (wheels + rotor) whenever the mower is active.
    // The mixer is updated only while _active, so animation pauses between sweeps.
    if (gltf.animations.length > 0) {
      this.mixer = new THREE.AnimationMixer(scene);
      const action = this.mixer.clipAction(gltf.animations[0]);
      action.timeScale = 6;
      action.play();
    }
  }
}
